import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { FiSend } from "react-icons/fi";
import { tagButtonStyle } from "@/components/FinancialShared/tableStyles";
import { formatDateTimeWithAt } from "@/utils/dateFormatter";
import { phoneFormatter } from "@/utils/formatPhone";
import { IHistoricoNotificacaoCcbItem } from "@/models/consignado/proposal-billings.model";
import {
  ehEmail,
  labelLinhasEvento,
  resolveChannelIcon,
  STATE_LABEL,
  STEP_COLORS,
  StepState,
} from "./notificacaoEvento.utils";

// Ícone do envio (por coluna/evento), com detalhes e ação de reenvio no
// popover. O item é o da rota REAL GET /proposta/cobrancas/:ccb/historico-
// notificacoes (TAR-050); o reenvio chama POST /notificacoes/:id/reenviar via
// `onReenviar` (a seção dona da query trata toast e invalidação).
type Props = {
  item: IHistoricoNotificacaoCcbItem;
  // Envios anteriores do mesmo tipo na mesma competência (do mais antigo pro
  // mais recente); só aparecem como registro no popover.
  anteriores?: IHistoricoNotificacaoCcbItem[];
  state: StepState;
  onReenviar: (item: IHistoricoNotificacaoCcbItem) => Promise<unknown>;
};

const rotuloRegistro = (anterior: IHistoricoNotificacaoCcbItem) => {
  const status = (anterior.status ?? "").toUpperCase();
  if (status === "PENDENTE" || status === "ENVIANDO") return "Pendente";
  return anterior.status_envio === "SUCCESS" ? "Enviado" : "Falha";
};

export default function NotificacaoIconCell({ item, anteriores = [], state, onReenviar }: Props) {
  const [reenviando, setReenviando] = useState(false);

  const handleReenviar = async () => {
    setReenviando(true);
    try {
      await onReenviar(item);
    } catch {
      // A seção já mostrou o toast de erro.
    } finally {
      setReenviando(false);
    }
  };

  const colors = STEP_COLORS[state];
  const [linha1, linha2] = labelLinhasEvento(item.tipo);
  const ChannelIcon = resolveChannelIcon(item.canal, item.tipo);
  const sucesso = item.status_envio === "SUCCESS";
  const erro = state === "error";
  const destinatarioEhEmail = ehEmail(item.destinatario ?? "");

  return (
    <Popover placement="top" trigger="hover" isLazy>
      <PopoverTrigger>
        <Flex
          w={8}
          h={8}
          bg={colors.bg}
          color={colors.color}
          borderRadius="full"
          align="center"
          justify="center"
          cursor="pointer"
        >
          <Icon as={ChannelIcon} boxSize={4} />
        </Flex>
      </PopoverTrigger>
      <PopoverContent
        bg="white"
        color="gray.700"
        borderColor="gray.200"
        boxShadow="lg"
        maxW="320px"
        // Dentro do TableContainer (white-space: nowrap) — sem isso o texto
        // do popover não quebra e vaza pra fora da caixa.
        whiteSpace="normal"
        _focus={{ outline: "none", boxShadow: "lg" }}
      >
        <PopoverArrow bg="white" />
        <PopoverBody>
          <Box textAlign="left" fontFamily="Lexend Deca" wordBreak="break-word">
            <Text fontSize="xs" fontWeight="bold" mb={1} wordBreak="break-all">
              {linha1} {linha2}
            </Text>

            {item.destinatario && (
              <Text fontSize="xs" wordBreak="break-all">
                <Text as="span" color="gray.500">
                  {destinatarioEhEmail ? "E-mail: " : "Telefone: "}
                </Text>
                {destinatarioEhEmail ? item.destinatario : phoneFormatter(item.destinatario)}
              </Text>
            )}

            <Text fontSize="xs">
              <Text as="span" color="gray.500">Canal: </Text>
              {item.canal}
            </Text>

            {item.data_hora && (
              <Text fontSize="xs">
                <Text as="span" color="gray.500">Data do envio: </Text>
                {formatDateTimeWithAt(new Date(item.data_hora))}
              </Text>
            )}

            <Text fontSize="xs">
              <Text as="span" color="gray.500">Status do envio: </Text>
              <Text as="span" color={sucesso ? "green.600" : state === "future" ? "gray.600" : "red.500"}>
                {sucesso ? "Enviado com sucesso" : state === "future" ? "Pendente" : "Falha no envio"}
              </Text>
            </Text>

            {item.erro && (
              <Text fontSize="xs" color="red.500" wordBreak="break-word">
                <Text as="span" color="gray.500">Erro: </Text>
                {item.erro}
              </Text>
            )}

            {anteriores.map((anterior, indice) => (
              <Text key={anterior.id ?? indice} fontSize="xs">
                <Text as="span" color="gray.500">Registro anterior: </Text>
                <Text as="span" color={rotuloRegistro(anterior) === "Falha" ? "red.500" : undefined}>
                  {rotuloRegistro(anterior)}
                </Text>
                {anterior.data_hora && ` em ${formatDateTimeWithAt(new Date(anterior.data_hora))}`}
              </Text>
            ))}

            <Text fontSize="xs" mt={1}>
              <Text as="span" color="gray.500">Estado no fluxo: </Text>
              <Text as="span" fontStyle="italic">
                {STATE_LABEL[state]}
              </Text>
            </Text>

            <Button
              mt={3}
              size="xs"
              w="100%"
              {...tagButtonStyle(erro ? "red" : "green")}
              leftIcon={reenviando ? <Spinner size="xs" /> : <FiSend />}
              isDisabled={reenviando}
              onClick={handleReenviar}
              fontFamily="Lexend Deca"
              fontWeight="normal"
              px={2}
            >
              Reenviar notificação
            </Button>
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
